import Joi from 'joi';
import { TFunction } from 'react-i18next';
import {
  ImageFileMeta,
  LogoShapeConstraint,
  MAX_LOGO_FILE_SIZE_BYTES,
  ACCEPTED_LOGO_MIME_TYPES,
  SQUARE_LOGO_MIN_DIMENSION_PX,
  SQUARE_LOGO_MAX_DIMENSION_PX,
  LogoValidationResult,
} from './CompanyLogoType';

export const readImageFileMeta = (file: File, t: TFunction<'uploadCompanyLogo'>): Promise<ImageFileMeta> => {
  return new Promise<ImageFileMeta>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = (): void => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        file,
        sizeBytes: file.size,
        mimeType: file.type,
        widthPx: image.naturalWidth,
        heightPx: image.naturalHeight,
      });
    };

    image.onerror = (): void => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(t('errors.readingFileError')));
    };

    image.src = objectUrl;
  });
};

export const buildLogoJoiSchema = (
  shapeConstraint: LogoShapeConstraint,
  t: TFunction<'uploadCompanyLogo'>,
): Joi.ObjectSchema<ImageFileMeta> => {
  const baseSchema = Joi.object<ImageFileMeta>({
    file: Joi.any().required(),
    sizeBytes: Joi.number()
      .max(MAX_LOGO_FILE_SIZE_BYTES)
      .required()
      .messages({
        'number.max': t('validationErrors.maxSize'),
      }),
    mimeType: Joi.string()
      .valid(...ACCEPTED_LOGO_MIME_TYPES)
      .required()
      .messages({
        'any.only': t('validationErrors.fileType'),
      }),
    widthPx: Joi.number().integer().positive().required(),
    heightPx: Joi.number().integer().positive().required(),
  });

  if (shapeConstraint === 'square') {
    return baseSchema
      .append<ImageFileMeta>({
        widthPx: Joi.number()
          .integer()
          .min(SQUARE_LOGO_MIN_DIMENSION_PX)
          .max(SQUARE_LOGO_MAX_DIMENSION_PX)
          .required()
          .messages({
            'number.min': t('validationErrors.minDimensions', { min: SQUARE_LOGO_MIN_DIMENSION_PX }),
            'number.max': t('validationErrors.maxDimensions', { max: SQUARE_LOGO_MAX_DIMENSION_PX }),
          }),
      })
      .custom((value: ImageFileMeta, helpers) => {
        if (value.widthPx !== value.heightPx) {
          return helpers.error('logo.aspectRatio');
        }
        return value;
      }, 'square aspect ratio check')
      .messages({
        'logo.aspectRatio': t('validationErrors.aspectRatio'),
      });
  }

  return baseSchema;
};

export const validateLogoFile = async (
  file: File,
  shapeConstraint: LogoShapeConstraint,
  t: TFunction<'uploadCompanyLogo'>,
): Promise<LogoValidationResult> => {
  try {
    const meta = await readImageFileMeta(file, t);
    const schema = buildLogoJoiSchema(shapeConstraint, t);
    const { error } = schema.validate(meta, { abortEarly: false });

    if (!error) {
      return { isValid: true, errors: [] };
    }

    return {
      isValid: false,
      errors: error.details.map((detail) => detail.message),
    };
  } catch (readError) {
    // eslint-disable-next-line
    const message = readError instanceof Error ? readError.message : t('validationErrors.unableToValidate');
    return { isValid: false, errors: [message] };
  }
};
